import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ApiClient } from '../utils/apiClient';
import useAnalyticsErrorHandler from './useAnalyticsErrorHandler';
import { 
  generateFallbackCallSuccessAnalytics, 
  generateFallbackFailureReasons,
  isFallbackData 
} from '../utils/analyticsFallbacks';

const useCallSuccessAnalytics = (timeRange = 'billing_period', parrotId = null, phoneNumber = null) => {
  const { getToken } = useAuth();
  const apiClient = useRef(new ApiClient(getToken)).current;
  const { error, handleError, retry: retryError, clearError, canRetry, isRetrying } = useAnalyticsErrorHandler();
  
  const [callSuccessData, setCallSuccessData] = useState(null);
  const [failureReasonsData, setFailureReasonsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastSuccessfulFetch, setLastSuccessfulFetch] = useState(null);

  const fetchCallSuccessAnalytics = useCallback(async () => {
    setLoading(true);
    clearError();
    
    try {
      const params = new URLSearchParams({ timeRange });
      
      if (parrotId) params.append('parrotId', parrotId);
      if (phoneNumber) params.append('phoneNumber', phoneNumber);
      
      // Fetch both call success and failure reasons data
      const [callSuccessResponse, failureReasonsResponse] = await Promise.all([
        apiClient.get(`/api/analytics/call-success?${params.toString()}`),
        apiClient.get(`/api/analytics/failure-reasons?${params.toString()}`)
      ]);
      
      // Extract the actual analytics data from the response
      // The response now consistently has data in the 'data' property
      const extractedCallSuccessData = callSuccessResponse.data;
      const extractedFailureReasonsData = failureReasonsResponse.data;
      
      setCallSuccessData(extractedCallSuccessData);
      setFailureReasonsData(extractedFailureReasonsData);
      setLastSuccessfulFetch(new Date());
    } catch (err) {
      const displayError = await handleError(err, 'fetch_call_success', {
        timeRange,
        parrotId,
        phoneNumber
      });
      
      // Provide fallback data if no previous successful data exists
      setCallSuccessData(prevData => {
        if (!prevData || isFallbackData(prevData)) {
          return generateFallbackCallSuccessAnalytics(timeRange);
        }
        return prevData; // Keep existing data on error
      });
      
      setFailureReasonsData(prevData => {
        if (!prevData || isFallbackData(prevData)) {
          return generateFallbackFailureReasons();
        }
        return prevData; // Keep existing data on error
      });
    } finally {
      setLoading(false);
    }
  }, [apiClient, timeRange, parrotId, phoneNumber, handleError, clearError]); // Removed callSuccessData dependency!

  const retry = useCallback(async () => {
    await retryError(fetchCallSuccessAnalytics);
  }, [retryError, fetchCallSuccessAnalytics]);

  useEffect(() => {
    fetchCallSuccessAnalytics();
  }, [fetchCallSuccessAnalytics]);

  return {
    callSuccessData,
    failureReasonsData,
    loading,
    error,
    retry,
    refresh: fetchCallSuccessAnalytics,
    canRetry,
    isRetrying,
    lastSuccessfulFetch,
    isFallbackData: callSuccessData ? isFallbackData(callSuccessData) : false
  };
};

export default useCallSuccessAnalytics;
